// ── RingOverlay — the broadcast overlay of one ring (#604) ───────────────────
// A transparent 1920×1080 frame for a source of a live stream, for example an
// OBS "Browser Source". The stream crew scales the source in its software.
// The page renders from useRingFeed, as the ring display does, so both show
// the same data. It is silent: no toast, no banner, no error text, no sound.
// The page is in English only, as the ring display (operator, 2026-10-15).
// The options are in the URL, because a browser source has no switch:
//   pos=bottom|top   the edge of the main bar (default bottom).
//   top=0            no top bar.
//   timer=0          no backup timer.
//   demo=live|result|next   invented data, to place the source before a bout.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import en from '../i18n/en';
import { useRingFeed, type RingFeed } from '../hooks/useRingFeed';
import { formatClock } from '../hooks/useRoundClock';
import { boutCorner } from '../utils/boutCorner';
import FitText from '../components/ring/FitText';
import SilentBoundary from '../components/ring/SilentBoundary';
import {
  FRAME_H, FRAME_W, PANEL_COLOR, PANEL_SOFT, TIMER_COLOR, classLine, countryBorder, panelStateOf, ringViewOf, ringWords,
  roundFormulaOf, roundWinner, softText, stopRound, useFrameScale, type Corner, type PanelState, type RingWords,
} from '../components/ring/ringView';
import type { Bout, BoutVerdict, PublicRoundScore } from '../types';

/** The largest size of a last name, and the height of its row: one line at
 *  that size (leading-none). Change the two together. */
const NAME_MAX = 66;
const NAME_ROW = 'h-[66px]';

/** One boxer of the main bar, in the bright look of the ring display (#608).
 *  The country is a code pill, because OBS on Windows draws no flag emoji
 *  (#527). The last name fits its room. The first name and the team share one
 *  line in each state, with the team toward the middle (operator, 2026-10-15). The Winner label of a result
 *  sits at the top edge of the panel, out of the flow, so it moves no name
 *  (operator, 2026-10-15). `top-2` keeps the label above the room of the
 *  marks of the last name (Ž, Č, Š, Ė). */
function Boxer({ bout, corner, state, w }: { bout: Bout; corner: Corner; state: PanelState; w: RingWords }) {
  const { last, first, team, country } = boutCorner(bout, corner);
  const right = corner === 'blue';
  const soft = `font-normal ${softText(corner, state)}`;
  // The first name shows whole; a long team ends with "…".
  const firstPart = <span data-testid={`overlay-${corner}-first`} className="max-w-full shrink-0 truncate">{first}</span>;
  const teamPart = team && <span data-testid={`overlay-${corner}-team`} className={`min-w-0 truncate ${soft}`}>{team}</span>;
  const dot = team && <span className={`shrink-0 whitespace-pre ${soft}`}> · </span>;
  // The country pill has its own column. The last name and the line below it
  // share the other column, so the first name starts under the last name, not
  // under the pill (operator, 2026-10-15). Blue mirrors red.
  const cols = !country ? 'grid-cols-1' : right ? 'grid-cols-[minmax(0,1fr)_auto]' : 'grid-cols-[auto_minmax(0,1fr)]';
  const nameCol = country && !right ? 'col-start-2' : 'col-start-1';
  return (
    <div data-testid={`overlay-${corner}`} data-state={state}
      className={`relative flex h-full min-w-0 flex-1 flex-col justify-center px-8 ${right ? 'text-right' : ''} ${PANEL_COLOR[corner][state]}`}>
      {state === 'winner' && (
        <span data-testid={`overlay-${corner}-winner`} className={`absolute top-2 ${right ? 'right-8' : 'left-8'} rounded-md bg-white px-3 py-1 text-[22px] font-black uppercase leading-none tracking-[0.25em] ${PANEL_SOFT[corner].winner}`}>
          {w.T.common.winner}
        </span>
      )}
      <div className={`grid ${cols} items-center gap-x-4 gap-y-1.5`}>
        {country && (
          <span className={`row-start-1 ${right ? 'col-start-2' : 'col-start-1'} rounded-lg border-2 px-2 text-[28px] font-extrabold leading-[40px] tracking-[0.06em] ${countryBorder(corner, state)}`}>
            {country.toUpperCase()}
          </span>
        )}
        {/* #608 — the last name fits its room, as on the ring display. One
            height for each name (NAME_ROW): a long name of a smaller size
            keeps the line below in the place of the other corner. */}
        <div className={`row-start-1 ${nameCol} flex ${NAME_ROW} min-w-0 items-center`}>
          <FitText text={last || '—'} max={NAME_MAX} min={40} className="font-black uppercase leading-none" testId={`overlay-${corner}-last`} />
        </div>
        {/* The first name at the outer edge and the team toward the middle, so
            the two corners mirror each other (operator, 2026-10-15). */}
        <span data-testid={`overlay-${corner}-line`} className={`row-start-2 ${nameCol} flex min-w-0 text-[32px] font-bold leading-tight ${right ? 'justify-end' : ''}`}>
          {right ? <>{teamPart}{dot}{firstPart}</> : <>{firstPart}{dot}{teamPart}</>}
        </span>
      </div>
    </div>
  );
}

/** The middle panel while the bout is live: one row for each approved round, or VS.
 *  Four or five rows (#602) take a smaller size. Five rows of 48 px do not
 *  fit in the 220 px bar. Five rows of 36 px take 196 px. */
function Rounds({ scores, w }: { scores: PublicRoundScore[]; w: RingWords }) {
  if (!scores.length) {
    return <span className="text-[48px] font-black uppercase tracking-widest text-slate-400">{w.T.common.vs}</span>;
  }
  const small = scores.length > 3;
  return (
    <div className="flex flex-col items-stretch gap-1">
      {scores.map(s => {
        const won = roundWinner(s);
        return (
          <div key={s.round_number} data-testid={`overlay-round-${s.round_number}`}
            className={`grid grid-cols-[1fr_auto_1fr] items-center gap-4 font-black leading-none ${small ? 'text-[36px]' : 'text-[48px]'}`}>
            <span className={`justify-self-end rounded-lg px-3 ${won === 'red' ? 'bg-red-600 text-white' : ''}`}>{s.red_points}</span>
            <span className={`font-extrabold text-slate-500 ${small ? 'text-[22px]' : 'text-[26px]'}`}>R{s.round_number}</span>
            <span className={`justify-self-start rounded-lg px-3 ${won === 'blue' ? 'bg-blue-600 text-white' : ''}`}>{s.blue_points}</span>
          </div>
        );
      })}
    </div>
  );
}

/** Invented data for `?demo=`, so the stream crew can place the source. */
function demoFeed(kind: string): RingFeed {
  const bout = {
    id: 'demo', bout_number: 12, stage: 'Final', status: 'current', ring_name: 'A', rounds: 3,
    bout_age_group: 'Elite', bout_weight_class: '-71', red_gender: 'Male', blue_gender: 'Male',
    red_first_name: 'Demo', red_last_name: 'Red', red_team_name: 'Demo Club A', red_country: 'LT',
    blue_first_name: 'Demo', blue_last_name: 'Blue', blue_team_name: 'Demo Club B', blue_country: 'LV',
  } as unknown as Bout;
  const scores = [
    { round_number: 1, red_points: 10, blue_points: 9 },
    { round_number: 2, red_points: 9, blue_points: 10 },
  ] as PublicRoundScore[];
  const base: RingFeed = {
    ringName: 'A', currentBout: bout, publicScores: scores, flashResult: null,
    timerLeft: 151_000, timerPhase: 'running', nextBout: null,
  };
  if (kind === 'result') return { ...base, flashResult: { winner: 'red', outcome: 'WP', round_ended: 3 } as BoutVerdict };
  if (kind === 'next') return { ...base, currentBout: null, publicScores: [], timerLeft: null, nextBout: { ...bout, bout_number: 13, status: 'pending' } as Bout };
  return base;
}

function Overlay() {
  const { tournamentId, ringId } = useParams<{ tournamentId: string; ringId: string }>();
  const [params] = useSearchParams();
  const w = ringWords(en);
  const demo = params.get('demo');
  const live = useRingFeed(demo ? undefined : tournamentId, demo ? undefined : ringId, 'RingOverlay', { next: true });
  const feed = demo ? demoFeed(demo) : live;
  const atTop = params.get('pos') === 'top';
  const showTop = params.get('top') !== '0';
  const showTimer = params.get('timer') !== '0';
  const scale = useFrameScale();

  // The page is transparent: the video of the stream shows through it.
  useEffect(() => {
    document.documentElement.classList.add('ring-overlay');
    return () => document.documentElement.classList.remove('ring-overlay');
  }, []);

  const view = ringViewOf(feed);
  const bout = view.kind === 'none' ? null : view.bout;
  const timer = showTimer && view.kind === 'live' && feed.timerLeft !== null ? feed.timerLeft : null;
  const winner = view.kind === 'result' ? view.verdict.winner : null;
  const stop = bout && view.kind === 'result' ? stopRound(bout, view.verdict) : null;

  // #608 — the bright look of the ring display: white pills with a shadow, over the video.
  const line = bout ? classLine(bout, w) : '';
  const formula = bout ? roundFormulaOf(bout) : null;
  const topBar = bout && showTop && (
    <div data-testid="overlay-top" className="flex items-center gap-3 text-[28px] font-extrabold uppercase text-slate-900">
      {/* The name of the ring, as the ring display shows it ("Ring A" or "A"). */}
      {feed.ringName && <span className="rounded-lg bg-slate-900 px-5 py-1.5 text-white shadow-lg">{feed.ringName}</span>}
      <span className="rounded-lg bg-white px-5 py-1.5 shadow-lg">{w.T.common.bout} {bout.bout_number}</span>
      {bout.stage && <span className="rounded-lg bg-amber-500 px-5 py-1.5 shadow-lg">{w.stage(bout.stage)}</span>}
      {/* normal-case: the class line reads as on the ring display ("Male · Elite · -71"). */}
      {line && <span className="rounded-lg bg-white px-5 py-1.5 normal-case shadow-lg">{line}</span>}
      {formula && <span className="rounded-lg bg-white px-5 py-1.5 normal-case shadow-lg">{formula}</span>}
    </div>
  );

  const mainBar = bout && (
    // With pos=top, the timer sits under the bar, so the bar does not move when the timer appears.
    <div data-testid="overlay-main" data-view={view.kind} className={`flex ${atTop ? 'flex-col-reverse' : 'flex-col'} items-center gap-3`}>
      {timer !== null && (
        <span data-testid="overlay-timer" data-phase={feed.timerPhase}
          className={`rounded-xl bg-white px-8 py-1 font-mono text-[56px] font-black tabular-nums leading-tight shadow-xl ${TIMER_COLOR[feed.timerPhase]}`}>
          {formatClock(timer)}
        </span>
      )}
      <div className="flex h-[220px] w-full overflow-hidden rounded-3xl shadow-2xl">
        <Boxer bout={bout} corner="red" state={panelStateOf(winner, 'red')} w={w} />
        <div className="flex w-[360px] shrink-0 flex-col items-center justify-center gap-2 bg-white px-4 text-center text-slate-900">
          {view.kind === 'live' && <Rounds scores={view.scores} w={w} />}
          {view.kind === 'result' && (winner === 'draw'
            ? <span data-testid="overlay-draw" className="text-[56px] font-black uppercase">{w.T.common.draw}</span>
            : <>
                <span data-testid="overlay-outcome" className="text-[56px] font-black leading-none">{view.verdict.outcome}</span>
                {w.outcome(view.verdict.outcome) && <span className="text-[28px] font-bold leading-tight text-slate-700">{w.outcome(view.verdict.outcome)}</span>}
                {stop && (
                  <span className="rounded-md bg-slate-900 px-3 text-[24px] font-extrabold uppercase text-white">
                    {w.T.bout.roundN.replace('{n}', String(stop))}
                  </span>
                )}
              </>)}
          {view.kind === 'next' && (
            <>
              <span className="rounded-md bg-amber-500 px-3 text-[26px] font-black uppercase tracking-[0.25em]">{w.T.overlay.next}</span>
              <span data-testid="overlay-next-bout" className="text-[48px] font-black uppercase leading-none">{w.T.common.bout} {bout.bout_number}</span>
            </>
          )}
        </div>
        <Boxer bout={bout} corner="blue" state={panelStateOf(winner, 'blue')} w={w} />
      </div>
    </div>
  );

  // The title-safe area: 90 % of the frame, 96 px at each side, 54 px at the top and the bottom.
  return (
    <div className="pointer-events-none fixed left-0 top-0 origin-top-left select-none overflow-hidden"
      style={{ width: FRAME_W, height: FRAME_H, transform: `scale(${scale})` }} data-testid="ring-overlay" lang="en">
      <div className={`absolute inset-x-[96px] inset-y-[54px] flex ${atTop ? 'flex-col-reverse' : 'flex-col'} justify-between`}>
        <div className={`flex ${atTop ? 'items-end' : 'items-start'}`}>{topBar}</div>
        <div>{mainBar}</div>
      </div>
    </div>
  );
}

export default function RingOverlay() {
  return <SilentBoundary context="RingOverlay"><Overlay /></SilentBoundary>;
}
